import {
  TrackManager,
  GraphHopperRouter,
  ExtractFromSegmentProfiler,
  FallbackProfiler,
  SwisstopoProfiler,
  GraphHopperSnapper,
  SnappedDensifier,
} from '@geoblocks/edittrack';
import {getUid, type Feature, type MapBrowserEvent} from 'ol';
import type Map from 'ol/Map.js';
import {doubleClick, singleClick} from 'ol/events/condition.js';
import {edittrackMode$, visibleLayers$} from '../stores/store-map.ts';
import {getMainMap} from './creation.ts';
import {
  createGraphhopperRouterURL,
  createGraphhopperSnapperURL,
} from '../urlHandling/urlCreate.ts';
import {FactsProfiler} from './facts/profiler.ts';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import {Style, Stroke} from 'ol/style.js';
import type {LineString} from 'ol/geom';
import {
  tourStyleData$,
  resolveBikeSpeed,
  resolveWinterhikeSpeed,
  resolveHikeSpeed,
  resolveSnowshoeSpeed,
  resolveMountainBikeSpeed,
} from '../stores/store-tours.ts';
import type {POIMeta} from '../stores/store-main.ts';
import {DRAW_EXTENT} from './constants.ts';
import {createPopper} from '@popperjs/core/lib/popper.js';
import {renderTooltip} from '../helpers/tooltip.ts';
import {render} from 'lit';
import {msg} from '@lit/localize';
import type Point from 'ol/geom/Point.js';
import type {EventsKey} from 'ol/events';
import {unByKey} from 'ol/Observable.js';

let profile = 'schmneutral';
let manager: TrackManager<POIMeta>;
let graphHopperRouter: GraphHopperRouter;
let factProfiler: FactsProfiler;

const tooltipLabelText: Record<string, string> = {
  POI: msg('drag to move point', {
    desc: 'Tooltip POI on map in edit mode',
    id: 'tool-tip-drag-poi',
  }),
  controlPoint: msg('drag to move point', {
    desc: 'Tooltip for tour point on map in edit mode',
    id: 'tool-tip-drag-tour-point',
  }),
  segment: msg('drag to create point', {
    desc: 'Tooltip for segment in edit mode',
    id: 'tool-tip-drag-segment',
  }),
};

const validFeatureTypes = Object.keys(tooltipLabelText);

const deleteClasses = ['bg-white', 'flex', 'items-center', '!shadow-card'];

// Control points deletes from the delete tooltip
function deleteCondition(): boolean {
  return false;
}

function setGraphhopperProfile() {
  const targetLayers = ['veloland', 'wanderland', 'mtbland', 'skatingland'];
  visibleLayers$.subscribe((layers) => {
    const compare = layers.filter((l: string) => targetLayers.includes(l));
    const count = compare.length;

    profile = 'schmneutral';
    if (count === 1) {
      profile = `schm${compare[0].replace('land', '')}`;
    }
    if (count >= 2) {
      profile = 'schmall';
    }

    // Initial loading set the manager and router after, in that case we ignore.
    if (manager) {
      graphHopperRouter.url = createGraphhopperRouterURL(profile);
    }
  });
}

export function initTrackManager(
  map: Map,
  previewMap: boolean = false,
): TrackManager<POIMeta> {
  const layers = map.getLayers().getArray();
  const trackLayer = layers.find(
    (l) => l.get('name') === 'trackLayer',
  ) as VectorLayer<VectorSource>;
  const style = new Style({
    stroke: new Stroke({
      color: '#00cc33aa',
      width: 6,
    }),
  });

  const shadowSource = new VectorSource();
  const shadowTrackLayer = new VectorLayer({source: shadowSource, style});

  setGraphhopperProfile();
  graphHopperRouter = new GraphHopperRouter({
    map: map,
    url: createGraphhopperRouterURL(profile),
    maxRoutingTolerance: 15,
  });
  const graphhopperSnapper = new GraphHopperSnapper({
    map: map,
    url: createGraphhopperSnapperURL(),
    maxTolerance: 15,
  });
  const densifier = new SnappedDensifier({
    optimalPointDistance: 10,
    maxPointDistance: 80,
    maxPoints: 9980, // twice the limit for swisstopo calls -> 2 calls
  });

  const projection = map.getView().getProjection();
  const fallbackProfiler = new FallbackProfiler({
    profilers: [
      new ExtractFromSegmentProfiler({
        projection,
      }),
      new SwisstopoProfiler({
        projection: projection,
      }),
    ],
  });
  factProfiler = new FactsProfiler({
    bikeSpeed: resolveBikeSpeed,
    hikeSpeed: resolveHikeSpeed,
    winterhikeSpeed: resolveWinterhikeSpeed,
    snowshoeSpeed: resolveSnowshoeSpeed,
    mountainBikeSpeed: resolveMountainBikeSpeed,
    profiler: fallbackProfiler,
  });

  // don't assign to manager if it's the preview map
  if (previewMap) {
    return new TrackManager({
      map: map,
      router: graphHopperRouter,
      snapper: graphhopperSnapper,
      profiler: factProfiler,
      densifier: densifier,
      trackLayer: trackLayer,
      shadowTrackLayer: shadowTrackLayer,
      style: trackLayer.getStyleFunction(),
      deleteCondition: deleteCondition,
      addLastPointCondition: singleClick, // we have to use single click otherwise the double click is not fired
      addControlPointCondition: doubleClick,
      hitTolerance: 15,
      drawMaskColor: 'rgba(241, 245, 249, 1)',
      drawExtent: DRAW_EXTENT,
    });
  }

  manager = new TrackManager({
    map: map,
    router: graphHopperRouter,
    snapper: graphhopperSnapper,
    profiler: factProfiler,
    densifier: densifier,
    trackLayer: trackLayer,
    shadowTrackLayer: shadowTrackLayer,
    style: trackLayer.getStyleFunction(),
    deleteCondition: deleteCondition,
    addLastPointCondition: singleClick, // we have to use single click otherwise the double click is not fired
    addControlPointCondition: doubleClick,
    hitTolerance: 15,
    drawMaskColor: 'rgba(241, 245, 249, 1)',
    drawExtent: DRAW_EXTENT,
  });

  tourStyleData$.subscribe(() => {
    if (manager) {
      manager.render();
    }
  });

  // tooltip for track editing
  function generateGetBoundingClientRect(x = 0, y = 0) {
    return () => ({
      width: 0,
      height: 0,
      left: x,
      top: y,
      right: x,
      bottom: y,
    });
  }
  const virtualElement = {
    getBoundingClientRect: generateGetBoundingClientRect(),
  };

  const tooltip = renderTooltip('edittrack-tooltip', '', {
    showOnTouchDevices: true,
  });
  render(tooltip, document.body);
  const popper = document.querySelector('#edittrack-tooltip');
  const popperText = popper.querySelector('.tooltip-text');

  // @ts-ignore
  const instance = createPopper(virtualElement, popper, {
    placement: 'top',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 20],
        },
      },
    ],
  });

  let preventTooltip: boolean = null;
  let lastFeatureId: string = null;
  let deleteFeatureId: string = null;
  let mapMoveKey: EventsKey = null;

  const hideTooltip = () => {
    popper.classList.remove('opacity-100', 'visible', ...deleteClasses);
    popper.classList.add('opacity-0', 'invisible');
    deleteFeatureId = null;

    // pointer has moved out of the last point
    if (preventTooltip === true) {
      preventTooltip = false;
    }

    if (mapMoveKey) {
      unByKey(mapMoveKey);
      mapMoveKey = null;
    }
  };

  edittrackMode$.next('');
  edittrackMode$.subscribe((mode) => {
    if (mode) {
      manager.mode = mode;
    }
    if (mode !== 'edit') {
      hideTooltip();
    }
  });

  map.on('pointermove', (evt: MapBrowserEvent<MouseEvent>) => {
    if (manager.mode === 'edit') {
      if (evt.dragging || manager.submode === 'addpoi') {
        if (popper.classList.contains('visible')) hideTooltip();
        return;
      }
      const pixel = map.getEventPixel(evt.originalEvent);
      const features = map.getFeaturesAtPixel(pixel, {
        layerFilter: (layer) => layer === trackLayer,
      });
      if (features.length > 0) {
        const feature = features[0];
        if (!validFeatureTypes.includes(feature.get('type'))) {
          return;
        }
        if (deleteFeatureId) {
          if (deleteFeatureId === getUid(feature)) {
            return;
          } else {
            deleteFeatureId = null;
          }
        }
        if (feature.get('subtype') === 'last') {
          // don't display tooltip for the last point when the user just has added it.
          // the pointer has to move out and back in to display the tooltip.
          const uid = getUid(feature);
          if (preventTooltip === null || lastFeatureId !== uid) {
            // first time hovering over the last point
            preventTooltip = true;
            lastFeatureId = uid;
            return;
          }
          if (preventTooltip === true) {
            // second or more time hovering over the last point
            return;
          }
        }

        virtualElement.getBoundingClientRect = generateGetBoundingClientRect(
          evt.originalEvent.clientX,
          evt.originalEvent.clientY,
        );
        popper.classList.remove('opacity-0', 'invisible', ...deleteClasses);
        popper.classList.add('opacity-100', 'visible', 'rounded');
        popperText.innerHTML = `<span style="white-space: pre-line;">${tooltipLabelText[feature.get('type')]}</span>`;
        instance.update();
      } else if (!deleteFeatureId) {
        hideTooltip();
      }
    }
  });

  map.on('singleclick', (evt: MapBrowserEvent<MouseEvent>) => {
    if (manager.mode === 'edit') {
      const pixel = map.getEventPixel(evt.originalEvent);
      const features = map.getFeaturesAtPixel(pixel, {
        layerFilter: (layer) => layer === trackLayer,
      });
      if (features.length > 0) {
        const feature = features[0] as Feature<Point>;
        if (feature.get('type') !== 'controlPoint') {
          return;
        }
        preventTooltip = true;
        deleteFeatureId = getUid(feature);

        const featPixel = map.getPixelFromCoordinate(
          feature.getGeometry().getCoordinates(),
        );
        const mapBbox = map.getTargetElement().getBoundingClientRect();
        virtualElement.getBoundingClientRect = generateGetBoundingClientRect(
          featPixel[0] + mapBbox.left,
          featPixel[1] + mapBbox.top,
        );
        popper.classList.remove('opacity-0', 'invisible', 'rounded');
        popper.classList.add('opacity-100', 'visible', ...deleteClasses);
        popperText.innerHTML = `<svg
            xmlns="http://www.w3.org/2000/svg"
            class="fill-chm-red mouse:hover:fill-chm-focus-red w-[24px] h-[24px] cursor-pointer"
            viewBox="0 0 24 24"
          >
            <path
              d="M10 2 9 3H4v2h1v15a1.943 1.943 0 0 0 2 2h10a1.937 1.937 0 0 0 2-2V5h1V3h-5l-1-1ZM7 5h10v15H7Zm2 2v11h2V7Zm4 0v11h2V7Zm0 0"
            />
          </svg>`;
        popper.querySelector('svg').onclick = () => {
          manager.deleteControlPoint(feature);
          hideTooltip();
        };
        mapMoveKey = map.once('movestart', () => hideTooltip());
        instance.update();
      } else if (deleteFeatureId) {
        hideTooltip();
      }
    }
  });

  return manager;
}

export function getTrackManager(): TrackManager<POIMeta> {
  if (!manager) {
    const map = getMainMap();
    manager = initTrackManager(map);
  }
  return manager;
}

export async function computeProfile(segment: Feature<LineString>) {
  if (!factProfiler) return;
  await factProfiler.computeProfile(segment);
}
